import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Empty, StatusBadge } from "@/components/Bits";

function Section({ title, hint, rows, children, testid }) {
  return (
    <div className="kh-card" data-testid={testid}>
      <div className="flex items-baseline justify-between border-b px-5 py-4">
        <h3 className="font-display text-lg font-medium">{title} <span className="ml-1 font-mono text-sm text-muted-foreground">{rows.length}</span></h3>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </div>
      {rows.length === 0 ? <div className="p-5 text-sm text-muted-foreground">None.</div> : <div className="divide-y">{rows.map(children)}</div>}
    </div>
  );
}

export default function Followups() {
  const [d, setD] = useState(null);
  const load = () => api.get("/followups").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const call = async (p, msg, body) => { try { await api.post(p, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <>
      <PageHeader eyebrow="Weekly nudges · dead after 2 months · re-eligible after 1 year" title="Follow-ups" />
      {d.due.length + d.upcoming.length + d.needs_human.length + d.dead.length === 0 && <Empty testid="followups-empty">No sent emails yet. Mark drafts as sent (or sync Outlook) to start tracking.</Empty>}
      <Section title="Due now" hint="No reply — suggest a follow-up" rows={d.due} testid="followups-due">
        {(r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`followup-due-${r.id}`}>
            <div className="flex-1"><div className="font-medium">{r.lead_name}</div><div className="text-xs text-muted-foreground">{r.track_name} · sent {fmtDate(r.sent_at)} · {r.followups.length} follow-ups so far</div></div>
            <Button size="sm" onClick={() => call(`/drafts/${r.id}/followup`, "Follow-up draft created")} data-testid={`followup-create-${r.id}`}>Create follow-up draft</Button>
            <Button size="sm" variant="ghost" onClick={() => call(`/drafts/${r.id}/mark`, "Marked replied", { state: "replied" })} data-testid={`followup-replied-${r.id}`}>Got a reply</Button>
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => call(`/drafts/${r.id}/mark`, "Marked dead", { state: "dead" })} data-testid={`followup-dead-${r.id}`}>Mark dead</Button>
          </div>
        )}
      </Section>
      <Section title="Needs a human" hint="Replies waiting for you" rows={d.needs_human} testid="followups-human">
        {(r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`followup-human-${r.id}`}>
            <div className="flex-1"><div className="font-medium">{r.lead_name} <StatusBadge status="replied" /></div>
              <div className="mt-1 text-xs text-muted-foreground">{r.reply_from || r.to} · {fmtDate(r.replied_at)} {r.reply_preview && `— “${r.reply_preview.slice(0, 140)}”`}</div></div>
            <Button size="sm" variant="secondary" onClick={() => call(`/drafts/${r.id}/resolve`, "Marked handled")} data-testid={`followup-resolve-${r.id}`}>Handled</Button>
          </div>
        )}
      </Section>
      <Section title="Upcoming" hint="Next follow-up date" rows={d.upcoming} testid="followups-upcoming">
        {(r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm" data-testid={`followup-upcoming-${r.id}`}>
            <span className="flex-1">{r.lead_name} <span className="text-xs text-muted-foreground">· {r.track_name}</span></span>
            <span className="font-mono text-xs text-muted-foreground">due {fmtDate(r.followup_due_at)}</span>
            <Button size="sm" variant="ghost" onClick={() => call(`/drafts/${r.id}/followup`, "Follow-up draft created")} data-testid={`followup-early-${r.id}`}>Follow up now</Button>
            <Button size="sm" variant="ghost" onClick={() => call(`/drafts/${r.id}/mark`, "Marked replied", { state: "replied" })} data-testid={`followup-upcoming-replied-${r.id}`}>Got a reply</Button>
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => call(`/drafts/${r.id}/mark`, "Marked dead", { state: "dead" })} data-testid={`followup-upcoming-dead-${r.id}`}>Mark dead</Button>
          </div>
        )}
      </Section>
      <Section title="Dead leads" hint="No response after 2 months" rows={d.dead} testid="followups-dead">
        {(r) => (
          <div key={r.id} className="flex items-center justify-between px-5 py-3 text-sm text-muted-foreground">
            <span>{r.lead_name}</span><span className="font-mono text-xs">dead {fmtDate(r.dead_at)}</span>
          </div>
        )}
      </Section>
    </>
  );
}
